<template>
  <div class="advance-ledger-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Cheque Summary Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ summaryItems.length }} Vouchers
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">Loading report details...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Cheque Summary Report</span>
              <span class="date-range-text">
                <strong>{{ formatDate(dtFrom) }}</strong> to
                <strong>{{ formatDate(dtTo) }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Ledger Table -->
      <div class="ledger-table-wrap">
        <table class="ledger-table">
          <thead>
            <tr class="main-header">
              <th class="text-center col-sno">S.NO</th>
              <th class="text-center col-vno">V.NO</th>
              <th class="col-date">DATE</th>
              <th class="col-cheque">CHEQUE TITLE</th>
              <th class="col-desc">DESCRIPTION</th>
              <th class="text-right col-amount">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            <!-- Data Rows -->
            <template v-for="(item, index) in summaryItems">
              <tr :key="'entry-' + index" class="entry-row opening-row">
                <td class="text-center">{{ index + 1 }}</td>
                <td class="text-center cell-vno">
                  <a
                    href="#"
                    class="voucher-link"
                    @click.prevent="openVoucher(item.entry)"
                  >
                    {{ item.entry.voucherType }}-{{ item.entry.voucherNum }}
                  </a>
                </td>
                <td class="cell-date">{{ formatDate(item.entry.date) }}</td>
                <td class="cell-cheque font-weight-bold text-primary">
                  {{ item.entry.chequeTitle || "-" }}
                </td>
                <td class="cell-desc">
                  <div class="description-text">
                    {{ item.entry.description }}
                  </div>
                  <div v-if="item.entry.payee" class="payee-tag mt-50">
                    <span class="payee-label">PAYEE:</span>
                    <span class="payee-name">{{ item.entry.payee }}</span>
                  </div>
                </td>
                <td class="text-right font-weight-bold text-dark col-amount">
                  {{ formatCurrency(item.entry.amount) }}
                </td>
              </tr>
            </template>

            <!-- If No Data -->
            <tr v-if="summaryItems.length === 0">
              <td colspan="6" class="text-center py-4 text-muted">
                No cheques found for this period.
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td colspan="5" class="text-right font-weight-bold">
                TOTAL AMOUNT
              </td>
              <td class="text-right font-weight-bold col-amount">
                {{ formatCurrency(totalAmount) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "ChequeSummaryPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportData: {
        data: [],
      },
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      campusAddress: "",
      db: "",
      cID: "",
      dtFrom: "",
      dtTo: "",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    summaryItems() {
      const data = this.reportData.data || [];
      return data.filter(
        (item) =>
          item.entry && item.entry.chequeTitle && item.entry.chequeTitle !== "-"
      );
    },
    totalAmount() {
      return this.summaryItems.reduce((sum, item) => {
        return sum + (parseFloat(item.entry?.amount) || 0);
      }, 0);
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId;
      this.dtFrom = query.dtFrom;
      this.dtTo = query.dtTo;

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];

          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
          this.campusAddress = info.address;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let baseUrl = this.$store.state.domain;
        let url = "";

        if (baseUrl.endsWith("/api/")) {
          url = `${baseUrl}JournalEntry/GetChequeSummaryReport`;
        } else {
          if (!baseUrl.endsWith("/")) {
            baseUrl += "/";
          }
          url = `${baseUrl}api/JournalEntry/GetChequeSummaryReport`;
        }

        url = `${url}?db=${this.db}&cID=${this.cID}&dtFrom=${this.dtFrom}&dtTo=${this.dtTo}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.reportData = response.data;
        } else {
          this.error = response.data?.message || "Failed to load report data.";
        }
      } catch (error) {
        console.error("Failed to load cheque summary:", error);
        this.error =
          "Network error or server unavailable. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "0.00";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    openVoucher(item) {
      const entryID = item.id;
      if (!entryID) return;

      const baseUrl = `https://${this.reportDomain}.myskool.app`;
      const url = `${baseUrl}/Account/PrintEntryVoucher?entryID=${entryID}&dbb=${encodeURIComponent(
        this.db
      )}&cID=${this.cID}`;
      window.open(url, "_blank");
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.advance-ledger-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
  overflow-x: hidden;
  box-sizing: border-box;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 4rem 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
  overflow: hidden;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .d-flex {
    min-width: 0;
  }

  .header-text-block {
    min-width: 0;
    flex: 1;
  }

  .school-logo-img {
    height: 90px;
    width: auto;
    max-width: 120px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 0;
      flex-shrink: 0;
    }

    .date-range-text {
      color: #555;
      min-width: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }
}

.ledger-table-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 2rem;
}

.ledger-table {
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border: 1px solid #dee2e6;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 8px 10px;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
    vertical-align: top;
  }

  .col-sno {
    width: 5%;
  }
  .col-vno {
    width: 14%;
  }
  .col-date {
    width: 11%;
  }
  .col-cheque {
    width: 15%;
  }
  .col-desc {
    width: 40%;
  }
  .col-amount {
    width: 15%;
    white-space: nowrap;
  }

  .cell-vno,
  .cell-date,
  .cell-cheque,
  .cell-desc {
    line-height: 1.4;
  }

  .cell-desc {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .cell-cheque {
    overflow-wrap: anywhere;
  }

  thead {
    background: #2c3e50;
    th {
      padding: 14px 10px;
      color: white;
      font-weight: 700;
      text-align: left;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
      border: 1px solid #2c3e50;
      white-space: normal;
      vertical-align: middle;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f1f2f6;
      &:hover {
        background: #fdfdfd;
      }

      td {
        padding: 10px;
        font-size: 0.9rem;
        color: #2d3436;
      }

      &.opening-row {
        background-color: #f8f9fa;
      }
      &.details-row td {
        padding-top: 6px;
        padding-bottom: 6px;
        font-size: 0.88rem;
        border-top: none;
      }
    }
  }

  .status-badge {
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    &.approved {
      background: #e3fcec;
      color: #28a745;
    }
    &.pending {
      background: #fff3cd;
      color: #ffc107;
    }
    &.rejected {
      background: #f8d7da;
      color: #dc3545;
    }
  }

  .voucher-link {
    color: #000;
    text-decoration: none;
    font-weight: 700;
    word-break: break-all;
    display: inline-block;
    max-width: 100%;
    &:hover {
      text-decoration: underline;
      color: #2e86de;
    }
  }

  tfoot {
    .total-row {
      background: #f1f3f5;
      td {
        padding: 15px 10px;
        font-weight: 800;
        border-top: 2px solid #2c3e50;
        color: #2c3e50;
      }
    }
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

.description-text {
  line-height: 1.4;
  color: #2d3436;
  margin-bottom: 4px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.payee-tag {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 100%;
  background: #f1f2f6;
  border-radius: 4px;
  padding: 2px 8px;
  border-left: 3px solid #6c5ce7;

  .payee-label {
    font-size: 0.7rem;
    font-weight: 800;
    color: #636e72;
    margin-right: 6px;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }

  .payee-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: #2d3436;
    text-transform: capitalize;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
}

.text-right {
  text-align: right;
}
.text-center {
  text-align: center;
}
.font-weight-bold {
  font-weight: 700;
}
.text-italic {
  font-style: italic;
}
.bg-light {
  background-color: #f8f9fa;
}

@page {
  size: landscape;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .advance-ledger-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0.7cm 1.2cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-table-wrap {
    overflow: visible !important;
    margin-top: 1rem !important;
  }

  .ledger-table {
    font-size: 8.8pt !important;
    table-layout: fixed !important;
    width: 100% !important;
    margin-top: 10px !important;

    th,
    td {
      padding: 4px 6px !important;
      border: 1px solid #dee2e6 !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      white-space: normal !important;
    }

    .col-amount {
      white-space: nowrap !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      padding: 8px 6px !important;
    }

    .voucher-link {
      color: #000 !important;
      text-decoration: none !important;
    }

    tfoot .total-row {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .description-text {
    line-height: 1.3 !important;
    margin-bottom: 3px !important;
    font-size: 8.8pt !important;
  }

  .payee-tag {
    padding: 1px 6px !important;
    margin-top: 3px !important;
    border-left-width: 2px !important;

    .payee-label {
      font-size: 0.65rem !important;
    }

    .payee-name {
      font-size: 0.8rem !important;
    }
  }

  .report-header-modern {
    margin-bottom: 5px !important;
    padding-bottom: 10px !important;
    .school-logo-img {
      height: 55px !important;
    }
    .school-name-full {
      font-size: 1.3rem !important;
    }
    .report-info-line {
      font-size: 0.85rem !important;
    }
  }

  .page-footer {
    display: none;
  }
}
</style>
